<script setup lang="ts">
import { reactive, ref, computed } from 'vue';
import type { FormInstance, FormRules } from 'element-plus';
import { message as ElMessage } from '../../utils/message';
import api from '../../api';
import AppAlert from '../ui/AppAlert.vue';
import SettingsSidebar from '../settings/SettingsSidebar.vue';

const loading     = ref(false);
const ruleFormRef = ref<FormInstance>();
const formError   = ref('');
const showOld     = ref(false);
const showNew     = ref(false);
const showConfirm = ref(false);

const ruleForm = reactive({ old_password: '', new_password: '', repeat_password: '' });

const validateConfirm = (_: any, value: string, callback: any) => {
  if (value !== ruleForm.new_password) callback(new Error('Passwords do not match'));
  else callback();
};

const rules = reactive<FormRules>({
  old_password:    [{ required: true, message: 'Current password is required', trigger: 'blur' }],
  new_password:    [{ required: true, min: 8, message: 'At least 8 characters', trigger: 'blur' }],
  repeat_password: [{ validator: validateConfirm, trigger: 'blur' }],
});

const strength = computed(() => {
  const p = ruleForm.new_password;
  if (!p) return 0;
  let s = 0;
  if (p.length >= 8)           s++;
  if (p.length >= 12)          s++;
  if (/[A-Z]/.test(p))         s++;
  if (/[0-9]/.test(p))         s++;
  if (/[^A-Za-z0-9]/.test(p))  s++;
  return s;
});
const strengthLabel = computed(() => ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'][strength.value] || '');
const strengthColor = computed(() => ['', '#ef4444', '#f59e0b', '#3b82f6', '#22c55e', '#16a34a'][strength.value] || '#e2e8f0');

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  await formEl.validate(async (valid) => {
    if (!valid) return;
    loading.value = true; formError.value = '';
    try {
      await api.post('customer/auth/password/change', {
        current_password: ruleForm.old_password,
        new_password:     ruleForm.new_password,
      });
      ElMessage.success('Password changed successfully');
      formEl.resetFields();
    } catch (err: any) {
      formError.value = err.response?.data?.message || 'Failed to change password.';
    } finally {
      loading.value = false;
    }
  });
};
</script>

<template>
  <div class="st-page">

    <div class="st-header">
      <div class="st-inner">
        <h1 class="st-title">Settings</h1>
        <p class="st-subtitle">Manage your account and business preferences</p>
      </div>
      <div class="st-header-rule"></div>
    </div>

    <div class="st-inner st-body">
      <SettingsSidebar />

      <div class="st-content">
        <div class="st-card" v-loading="loading">

          <!-- Header band -->
          <div class="st-band">
            <div class="st-band-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            </div>
            <div>
              <div class="st-band-title">Change Password</div>
              <div class="st-band-sub">Update your login credentials</div>
            </div>
          </div>

          <div class="st-form-body st-pw-layout">

            <!-- Form column -->
            <div class="st-pw-form">
              <AppAlert :message="formError" />

              <el-form ref="ruleFormRef" :model="ruleForm" :rules="rules" label-width="0px" status-icon>

                <div class="st-section-label">Current Password</div>
                <div class="st-field">
                  <label class="st-label">Enter your current password <span class="st-req">*</span></label>
                  <el-form-item prop="old_password" class="st-fi">
                    <div class="st-input-wrap">
                      <input v-model="ruleForm.old_password" class="st-input" :type="showOld ? 'text' : 'password'" placeholder="Current password" autocomplete="current-password" />
                      <button type="button" class="st-eye" @click="showOld = !showOld">
                        <svg v-if="!showOld" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      </button>
                    </div>
                  </el-form-item>
                </div>

                <div class="st-section-label" style="margin-top:20px;">New Password</div>
                <div class="st-field">
                  <label class="st-label">Choose a strong password <span class="st-req">*</span></label>
                  <el-form-item prop="new_password" class="st-fi">
                    <div class="st-input-wrap">
                      <input v-model="ruleForm.new_password" class="st-input" :type="showNew ? 'text' : 'password'" placeholder="At least 8 characters" autocomplete="new-password" />
                      <button type="button" class="st-eye" @click="showNew = !showNew">
                        <svg v-if="!showNew" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      </button>
                    </div>
                  </el-form-item>
                  <div class="st-strength" v-if="ruleForm.new_password">
                    <div class="st-strength-bars">
                      <div v-for="i in 5" :key="i" class="st-strength-bar" :style="{ background: i <= strength ? strengthColor : '#e2e8f0' }"></div>
                    </div>
                    <span class="st-strength-label" :style="{ color: strengthColor }">{{ strengthLabel }}</span>
                  </div>
                </div>

                <div class="st-field">
                  <label class="st-label">Confirm new password <span class="st-req">*</span></label>
                  <el-form-item prop="repeat_password" class="st-fi">
                    <div class="st-input-wrap">
                      <input v-model="ruleForm.repeat_password" class="st-input" :type="showConfirm ? 'text' : 'password'" placeholder="Repeat new password" autocomplete="new-password" />
                      <button type="button" class="st-eye" @click="showConfirm = !showConfirm">
                        <svg v-if="!showConfirm" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      </button>
                    </div>
                  </el-form-item>
                </div>

                <div class="st-actions">
                  <button class="st-btn" @click.prevent="submitForm(ruleFormRef)" :disabled="loading">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    Update Password
                  </button>
                </div>
              </el-form>
            </div>

            <!-- Tips column -->
            <div class="st-tips">
              <div class="st-tips-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0061eb" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              </div>
              <div class="st-tips-title">Password tips</div>
              <ul class="st-tips-list">
                <li>Use at least 8 characters</li>
                <li>Mix uppercase and lowercase</li>
                <li>Include numbers and symbols</li>
                <li>Avoid your name or email</li>
                <li>Don't reuse recent passwords</li>
              </ul>
              <div class="st-tips-note">After updating, you'll remain logged in on this device.</div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
* { box-sizing: border-box; }

.st-page { font-family: 'DM Sans', sans-serif; background: white; min-height: 100%; }
.st-inner { max-width: 1280px; margin: 0 auto; padding: 0 32px; }

.st-header { background: white; padding: 24px 0 0; }
.st-header .st-inner { padding-bottom: 18px; }
.st-title { font-size: 1.4rem; font-weight: 700; color: #0f1729; margin: 0 0 3px; }
.st-subtitle { font-size: 0.83rem; color: #94a3b8; margin: 0; }
.st-header-rule { height: 1.5px; background: #e8f0ff; }

.st-body { display: flex; gap: 24px; padding-top: 28px; padding-bottom: 40px; align-items: flex-start; }
.st-content { flex: 1; min-width: 0; }

.st-card { background: white; border-radius: 14px; border: 1.5px solid #e2e8f0; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.05); }

/* Band header */
.st-band {
  background: linear-gradient(130deg, #0061eb 0%, #003a96 100%);
  padding: 22px 28px; display: flex; align-items: center; gap: 16px;
}
.st-band-icon {
  width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
  background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25);
  display: flex; align-items: center; justify-content: center;
}
.st-band-title { font-size: 1rem; font-weight: 700; color: white; margin-bottom: 2px; }
.st-band-sub { font-size: 0.78rem; color: rgba(255,255,255,0.65); }

/* Two-column layout inside form body */
.st-form-body { padding: 28px 32px; }
.st-pw-layout { display: grid; grid-template-columns: 1fr 220px; gap: 28px; align-items: start; }
.st-pw-form { min-width: 0; }

.st-section-label {
  font-size: 0.67rem; font-weight: 700; color: #0061eb;
  text-transform: uppercase; letter-spacing: 0.08em;
  margin-bottom: 14px; padding-bottom: 8px; border-bottom: 1.5px solid #e8f0ff;
}
.st-field { margin-bottom: 16px; }
.st-label { display: block; font-size: 0.78rem; font-weight: 600; color: #475569; margin-bottom: 6px; }
.st-req { color: #dc2626; }
.st-fi { margin-bottom: 0 !important; }
:deep(.st-fi .el-form-item__content) { display: block; width: 100%; }
:deep(.st-fi .el-form-item__error) { padding-top: 4px; font-size: 0.74rem; }

.st-input-wrap { position: relative; display: flex; align-items: center; }
.st-input {
  width: 100%; height: 42px; padding: 0 42px 0 14px;
  border: 1.5px solid #e2e8f0; border-radius: 9px;
  font-family: 'DM Sans', sans-serif; font-size: 0.87rem; color: #1e293b;
  outline: none; transition: border-color 0.15s, box-shadow 0.15s; background: white;
}
.st-input:focus { border-color: #0061eb; box-shadow: 0 0 0 3px rgba(0,97,235,0.08); }
.st-input::placeholder { color: #94a3b8; }
.st-input:-webkit-autofill,
.st-input:-webkit-autofill:hover,
.st-input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0px 1000px white inset !important;
  -webkit-text-fill-color: #1e293b !important;
  transition: background-color 5000s ease-in-out 0s;
}
.st-eye {
  position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
  background: none; border: none; color: #94a3b8; cursor: pointer; padding: 2px;
  display: flex; align-items: center; transition: color 0.15s;
}
.st-eye:hover { color: #475569; }

.st-strength { display: flex; align-items: center; gap: 10px; margin-top: 8px; }
.st-strength-bars { display: flex; gap: 4px; }
.st-strength-bar { width: 28px; height: 4px; border-radius: 2px; transition: background 0.2s; }
.st-strength-label { font-size: 0.72rem; font-weight: 600; transition: color 0.2s; }

.st-actions { display: flex; justify-content: flex-end; margin-top: 24px; padding-top: 18px; border-top: 1px solid #f1f5f9; }
.st-btn {
  height: 42px; padding: 0 28px;
  background: #0061eb; color: white; border: none; border-radius: 9px;
  font-family: 'DM Sans', sans-serif; font-size: 0.88rem; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 8px; transition: background 0.15s;
}
.st-btn:hover:not(:disabled) { background: #0050c7; }
.st-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Tips panel */
.st-tips {
  background: #f8faff; border: 1.5px solid #ccdeff; border-radius: 12px;
  padding: 20px 16px;
}
.st-tips-icon { margin-bottom: 10px; }
.st-tips-title { font-size: 0.85rem; font-weight: 700; color: #0061eb; margin-bottom: 12px; }
.st-tips-list {
  margin: 0 0 14px; padding: 0 0 0 16px;
  font-size: 0.78rem; color: #475569; line-height: 2;
}
.st-tips-note {
  font-size: 0.73rem; color: #94a3b8; line-height: 1.6;
  padding-top: 12px; border-top: 1px solid #dce8ff;
}
</style>
